import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BookOpenText,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Info,
  CircleOff,
  IndianRupee,
  FileText,
  Filter,
  Gauge,
  LucideAngularModule,
  Play,
  RefreshCcw,
  TrendingUp,
  TriangleAlert,
  X,
  Zap,
} from 'lucide-angular';
import { Contract, Loa, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';

type ViewTab = 'overview' | 'contract-revision' | 'exchange-bids';

type SummaryCard = {
  title: string;
  icon: typeof FileText;
  accent: 'blue' | 'green';
  lines: Array<{ label: string; value: string; tone?: 'default' | 'orange' | 'green' }>;
};

type StatusCard = {
  title: string;
  subtitle: string;
  count: number;
  icon: typeof CircleAlert;
  tone: 'green' | 'blue' | 'amber' | 'red';
};

type ProfitBar = {
  contractId: string;
  value: number;
};

type ContractRevisionRow = {
  contractId: string;
  client: string;
  bessAsset: string;
  bessRole: string;
  timeBlock: string;
  market: 'DAM' | 'RTM';
  originalQty: number;
  revisedQty: number;
  surplus: number;
  contractRate: number;
  exchangePrice: number;
  priceSpread: number;
  penaltyRate: number;
  exchangeGain: number;
  dsmPenalty: number;
  netProfit: number;
  profitability: 'HIGH' | 'MODERATE' | 'MARGINAL' | 'NORMAL' | 'LOSS';
  recommendation: 'Surplus for Exchange' | 'Hold';
  strategy: string;
};

type ExchangeBidRow = {
  blockNo: number;
  timeBlock: string;
  totalSurplusMw: number;
  contractCount: number;
  market: 'DAM' | 'RTM';
  avgExchangePrice: number;
  exchangeGain: number;
  dsmPenalty: number;
  netProfit: number;
  profitability: 'HIGH' | 'MODERATE' | 'MARGINAL' | 'NORMAL' | 'LOSS';
};

type ProfitCategory = 'high' | 'moderate' | 'marginal' | 'normal' | 'loss';
type MarketFilter = 'DAM' | 'RTM';
type RevisionSelectionMode = 'all' | 'contractwise';

type ContractRevisionGroup = {
  contractId: string;
  seller: string;
  buyer: string;
  eligibleBlockCount: number;
  totalBlockCount: number;
  rows: ContractRevisionRow[];
};

type ContractHedgingSnapshotApi = {
  trade_date: string;
  generated_at: string | null;
  revisions: ContractRevisionRow[];
  exchange_bids: ExchangeBidRow[];
};

type ContractSupportRowApi = {
  contract_id: string;
  contract_number: string;
  contract_type: string;
  contract_category: string | null;
  issue_date: string;
  period_from: string;
  period_to: string;
  status: string;
  bess_vendor_id: string;
  bess_vendor_name: string;
  bess_role: string;
  counterparty_vendor_id: string;
  counterparty_vendor_name: string;
  quantum: number | null;
  agreement_type: string | null;
  energy_flow: string;
  rate_type?: string | null;
  loa_fixed_rate?: number | null;
  loa_mcp_value?: number | null;
};

type NetProfitModalData = {
  title: string;
  subtitle: string;
  equation: string;
  hint: string;
  details: Array<{ label: string; value: string; tone?: 'default' | 'good' | 'bad' }>;
};

@Component({
  standalone: true,
  selector: 'app-bess-contract-hedging-optimization-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="hedging-shell p-2 p-lg-2">
      <section class="hero-card">
        <div class="hero-copy">
          <div class="title-row">
            <h1>Contract Hedging & Optimization</h1>
            <button type="button" class="guide-link" (click)="showGuide()">
              <lucide-icon [img]="BookOpenTextIcon" [size]="14"></lucide-icon>
              <span>Help Guide</span>
            </button>
          </div>
          <p>Dynamic non-BESS contract quantity revision suggestions built from active seller contracts and forecast-backed exchange windows.</p>
          <div class="hero-meta">
            <span class="hero-pill">{{ activeSupportContractsCount() }} active contracts</span>
            <span class="hero-pill">{{ uniqueBessAssetsCount() }} seller entities</span>
            <span class="hero-pill">{{ approvedRevisionsCount() }} approved revisions</span>
            <span class="hero-pill">{{ snapshotPeriodLabel() }}</span>
          </div>
        </div>

        <div class="hero-actions">
          <label class="date-field">
            <span>Date</span>
            <input class="form-control" type="date" [min]="minAllowedDate()" [ngModel]="selectedDate()" (ngModelChange)="setDate($event)" />
          </label>
          <label class="date-field market-field">
            <span>Market</span>
            <select class="form-select" [ngModel]="selectedMarketFilter()" (ngModelChange)="setMarketFilter($event)">
              @for (option of marketFilterOptions; track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          </label>
          <button type="button" class="toolbar-btn" (click)="applyFilters()">
            <lucide-icon [img]="FilterIcon" [size]="15"></lucide-icon>
            <span>Filter</span>
          </button>
          <button type="button" class="run-btn" (click)="runOptimization()" [disabled]="loading()">
            <lucide-icon [img]="PlayIcon" [size]="15"></lucide-icon>
            <span>Run Optimization</span>
          </button>
          <button type="button" class="toolbar-btn" (click)="refresh()" [disabled]="loading()">
            <lucide-icon [img]="RefreshIcon" [size]="15"></lucide-icon>
            <span>Refresh</span>
          </button>
        </div>
      </section>

      <section class="tab-bar">
        @for (tab of tabs; track tab.id) {
          <button
            type="button"
            class="tab-btn"
            [class.active]="activeTab() === tab.id"
            (click)="activeTab.set(tab.id)"
          >
            <lucide-icon [img]="tab.icon" [size]="15"></lucide-icon>
            <span>{{ tab.label }}</span>
          </button>
        }
      </section>

      @if (activeTab() === 'overview') {
        <section class="summary-grid">
          @for (card of summaryCards(); track card.title) {
            <article class="summary-card" [class.summary-profit]="card.accent === 'green'">
              <div class="summary-head">
                <span>{{ card.title }}</span>
                <lucide-icon [img]="HelpIcon" [size]="14"></lucide-icon>
              </div>
              <div class="summary-body">
                <div class="summary-icon" [class.summary-icon-green]="card.accent === 'green'">
                  <lucide-icon [img]="card.icon" [size]="28"></lucide-icon>
                </div>
                @if (card.title === 'Total Contracts Analyzed') {
                  <div class="summary-highlight">
                    <strong>{{ totalContractsAnalyzed() }}</strong>
                    <small>{{ eligibleForRevisionCount() }} eligible, {{ approvedRevisionsCount() }} approved</small>
                  </div>
                } @else if (card.title === 'Total Net Profit') {
                  <div class="profit-highlight">
                    <span class="profit-value">{{ formatCompactRupees(totalNetProfit()) }}</span>
                    <div class="profit-breakup">
                      <div><span>Approved Exchange Gain:</span><strong>{{ formatSignedCompactRupees(totalExchangeGain()) }}</strong></div>
                      <div><span>Approved Deduction:</span><strong class="text-bad">{{ formatSignedCompactRupees(-totalDsmPenalty()) }}</strong></div>
                    </div>
                  </div>
                } @else {
                  <div class="metric-lines">
                    @for (line of card.lines; track line.label) {
                      <div class="metric-line">
                        <span>{{ line.label }}</span>
                        <strong [class.text-orange]="line.tone === 'orange'" [class.text-good]="line.tone === 'green'">{{ line.value }}</strong>
                      </div>
                    }
                  </div>
                }
              </div>
            </article>
          }
        </section>

        <section class="status-grid">
          @for (card of statusCards(); track card.title) {
            <article class="status-card" [class]="'tone-' + card.tone">
              <div class="status-icon">
                <lucide-icon [img]="card.icon" [size]="24"></lucide-icon>
              </div>
              <div class="status-copy">
                <strong>{{ card.count }}</strong>
                <h3>{{ card.title }}</h3>
                <p>{{ card.subtitle }}</p>
              </div>
            </article>
          }
        </section>

        @if (loading()) {
          <section class="placeholder-card">
            <div class="placeholder-icon"><lucide-icon [img]="GaugeIcon" [size]="26"></lucide-icon></div>
            <div><h2>Loading overview</h2><p>Computing contract revision opportunities for {{ selectedDate() }}.</p></div>
          </section>
        } @else if (filteredContractRevisionRows().length === 0) {
          <section class="placeholder-card">
            <div class="placeholder-icon"><lucide-icon [img]="GaugeIcon" [size]="26"></lucide-icon></div>
            <div><h2>{{ overviewEmptyTitle() }}</h2><p>{{ overviewEmptyMessage() }}</p></div>
          </section>
        } @else {
        <section class="chart-grid">
          <article class="chart-card">
            <div class="chart-head">
              <h2>Contract Profitability Distribution</h2>
              <p>Number of contracts by profit category</p>
            </div>
            <div class="distribution-card">
              <div class="pie-wrap">
                <div class="pie-chart" [style.background]="distributionGradient()"></div>
                <span class="pie-label pie-label-orange">Marginal: {{ marginalCount() }}</span>
                <span class="pie-label pie-label-red">Loss: {{ lossCount() }}</span>
                <span class="pie-label pie-label-blue">High Profit: {{ highProfitCount() }}</span>
              </div>
            </div>
          </article>

          <article class="chart-card">
            <div class="chart-head">
              <h2>Top 10 Contracts by Net Profit</h2>
              <p>Highest profit opportunities ranked</p>
            </div>
            <div class="bar-chart">
              @for (bar of topContracts(); track bar.contractId) {
                <div class="bar-row">
                  <span class="bar-label">{{ bar.contractId }}</span>
                  <div class="bar-track">
                    <div class="bar-fill" [style.width.%]="bar.value"></div>
                  </div>
                  <span class="bar-value">{{ topContractValue(bar.contractId) | number:'1.0-0' }}</span>
                </div>
              }
            </div>
          </article>
        </section>
        }
      } @else if (activeTab() === 'contract-revision') {
        <section class="revision-stack">
          <article class="info-banner">
            <div class="info-icon">
              <lucide-icon [img]="InfoIcon" [size]="16"></lucide-icon>
            </div>
            <div>
              <h2>How to Read This Table</h2>
              <p>Show contracts where reducing quantity and selling in exchange is profitable. Red rows indicate losses, maintain the original contract for these.</p>
            </div>
          </article>

          <article class="revision-card">
            <div class="revision-head exchange-table-head">
              <div>
                <h2>Contract Quantity Revision Suggestions for Non-BESS Contracts</h2>
                <p>Review original vs suggested revised quantities for contracts other than the BESS contract, along with profit calculations</p>
              </div>
              <div class="exchange-table-actions">
                <div class="selection-mode-toggle">
                  <button type="button" class="tab-btn" [class.active]="selectedRevisionMode() === 'all'" (click)="setRevisionSelectionMode('all')">All</button>
                  <button type="button" class="tab-btn" [class.active]="selectedRevisionMode() === 'contractwise'" (click)="setRevisionSelectionMode('contractwise')">Contractwise</button>
                </div>
                <label class="bulk-select-toggle">
                  <input
                    type="checkbox"
                    [checked]="showNotEligibleRevisions()"
                    (change)="toggleShowNotEligibleRevisions($any($event.target).checked)"
                  />
                  <span>Show Not Eligible</span>
                </label>
                <label class="bulk-select-toggle">
                  <input
                    type="checkbox"
                    [checked]="allDisplayedRevisionsSelected()"
                    [indeterminate]="hasPartialRevisionSelection()"
                    (change)="toggleAllRevisionSelections($any($event.target).checked)"
                  />
                  <span>Select all</span>
                </label>
                <button type="button" class="toolbar-btn" (click)="approveSelectedRevisions()" [disabled]="selectedRevisionCount() === 0">
                  <lucide-icon [img]="CircleCheckIcon" [size]="14"></lucide-icon>
                  <span>Approve Selected ({{ selectedRevisionCount() }})</span>
                </button>
              </div>
            </div>
            @if (selectedRevisionMode() === 'contractwise') {
            <div class="revision-accordion-wrap">
              @if (loading()) {
                <div class="revision-accordion-empty">Loading contract revision suggestions...</div>
              } @else if (contractRevisionGroups().length === 0) {
                <div class="revision-accordion-empty">{{ contractRevisionEmptyMessage() }}</div>
              } @else {
              @for (group of contractRevisionGroups(); track group.contractId) {
                <article class="revision-accordion-card">
                  <button type="button" class="revision-accordion-header" (click)="toggleRevisionContract(group.contractId)">
                    <div class="revision-accordion-main">
                      <span class="revision-accordion-chevron">{{ isRevisionContractExpanded(group.contractId) ? '▾' : '▸' }}</span>
                      <div>
                        <strong>{{ group.contractId }}</strong>
                        <small>{{ group.totalBlockCount }} total {{ group.totalBlockCount === 1 ? 'block' : 'blocks' }}, {{ group.eligibleBlockCount }} eligible</small>
                      </div>
                    </div>
                    <div class="revision-accordion-side">
                      <span>{{ group.seller }}</span>
                      <span>to {{ group.buyer }}</span>
                    </div>
                  </button>
                  @if (isRevisionContractExpanded(group.contractId)) {
                  <div class="revision-table-wrap">
                    <table class="revision-table revision-table-compact">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              class="exchange-select"
                              [checked]="isRevisionContractSelected(group.contractId)"
                              [indeterminate]="isRevisionContractPartiallySelected(group.contractId)"
                              [disabled]="group.eligibleBlockCount === 0"
                              (change)="toggleRevisionContractSelection(group.contractId, $any($event.target).checked)"
                            />
                          </th>
                          <th>S.No</th>
                          <th>Time Block</th>
                          <th>Market</th>
                          <th>Original Qty (MW)</th>
                          <th>Revised Qty (MW)</th>
                          <th>Surplus for Exchange</th>
                          <th>Contract Rate</th>
                          <th>Forecast Market Price</th>
                          <th>Penalty (Rs)</th>
                          <th><button type="button" class="calc-header-btn" (click)="openNetProfitFormulaModal('contract-revision')">Net Profit (Rs)</button></th>
                          <th>Profitability</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (row of group.rows; track row.contractId + '-' + row.timeBlock + '-' + row.market; let rowIndex = $index) {
                          <tr [class.row-loss]="row.profitability === 'LOSS'" [class.row-approved]="isRevisionApproved(row)">
                            <td>
                              <input
                                type="checkbox"
                                class="exchange-select"
                                [checked]="isRevisionSelected(row)"
                                [disabled]="!canApprove(row)"
                                (change)="toggleRevisionSelection(row, $any($event.target).checked)"
                              />
                            </td>
                            <td>{{ rowIndex + 1 }}</td>
                            <td>{{ formatMarketTimeBlock(row.timeBlock, row.market) }}</td>
                            <td><span class="market-pill" [class.market-rtm]="row.market === 'RTM'">{{ row.market }}</span></td>
                            <td>{{ formatMw(row.originalQty) }}</td>
                            <td class="cell-revised">{{ formatMw(row.revisedQty) }}</td>
                            <td class="cell-profit">{{ formatSignedMw(suggestedSurplus(row)) }}</td>
                            <td>{{ formatPrice(row.contractRate) }}</td>
                            <td class="cell-profit">{{ formatPrice(row.exchangePrice) }}</td>
                            <td class="cell-dsm">
                              <div class="penalty-cell">{{ formatSignedRupees(-row.dsmPenalty) }}<button type="button" class="penalty-info-btn" [attr.title]="penaltyBreakupLabel(row)" aria-label="Show penalty info">i</button></div>
                            </td>
                            <td class="cell-profit"><button type="button" class="calc-value-btn" [attr.title]="contractRevisionNetProfitTooltip(row)" (click)="openContractRevisionNetProfitModal(row)">{{ formatSignedRupees(row.netProfit) }}</button></td>
                            <td><span class="profit-pill" [class.profit-pill-normal]="row.profitability === 'NORMAL'">{{ row.profitability }}</span></td>
                            <td>
                              <button
                                type="button"
                                class="accept-btn"
                                [class.accept-btn-disabled]="!canApprove(row)"
                                [class.accept-btn-approved]="isRevisionApproved(row)"
                                (click)="toggleRevisionApproval(row)"
                              >
                                <lucide-icon [img]="CircleCheckIcon" [size]="13"></lucide-icon>
                                <span>{{ isRevisionApproved(row) ? 'Accepted' : canApprove(row) ? 'Accept' : 'Not Eligible' }}</span>
                              </button>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  }
                </article>
              }
              }
            </div>
            } @else {
            <div class="revision-table-wrap">
              <table class="revision-table revision-table-compact">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        class="exchange-select"
                        [checked]="allDisplayedRevisionsSelected()"
                        [indeterminate]="hasPartialRevisionSelection()"
                        (change)="toggleAllRevisionSelections($any($event.target).checked)"
                      />
                    </th>
                    <th>S.No</th>
                    <th>Contract ID</th>
                    <th>Seller</th>
                    <th>Buyer</th>
                    <th>Time Block</th>
                    <th>Market</th>
                    <th>Original Qty (MW)</th>
                    <th>Revised Qty (MW)</th>
                    <th>Surplus for Exchange</th>
                    <th>Contract Rate</th>
                    <th>Forecast Market Price</th>
                    <th>Penalty (Rs)</th>
                    <th><button type="button" class="calc-header-btn" (click)="openNetProfitFormulaModal('contract-revision')">Net Profit (Rs)</button></th>
                    <th>Profitability</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @if (loading()) {
                    <tr><td colspan="16" class="empty-state">Loading contract revision suggestions...</td></tr>
                  } @else if (displayedContractRevisionRows().length === 0) {
                    <tr><td colspan="16" class="empty-state">{{ contractRevisionEmptyMessage() }}</td></tr>
                  } @else {
                  @for (row of displayedContractRevisionRows(); track row.contractId + '-' + row.timeBlock + '-' + row.market; let rowIndex = $index) {
                    <tr [class.row-loss]="row.profitability === 'LOSS'" [class.row-approved]="isRevisionApproved(row)">
                      <td>
                        <input
                          type="checkbox"
                          class="exchange-select"
                          [checked]="isRevisionSelected(row)"
                          [disabled]="!canApprove(row)"
                          (change)="toggleRevisionSelection(row, $any($event.target).checked)"
                        />
                      </td>
                      <td>{{ rowIndex + 1 }}</td>
                      <td><div class="cell-contract">{{ row.contractId }}</div></td>
                      <td>{{ sellerLabel(row) }}</td>
                      <td>{{ buyerLabel(row) }}</td>
                      <td>{{ formatMarketTimeBlock(row.timeBlock, row.market) }}</td>
                      <td><span class="market-pill" [class.market-rtm]="row.market === 'RTM'">{{ row.market }}</span></td>
                      <td>{{ formatMw(row.originalQty) }}</td>
                      <td class="cell-revised">{{ formatMw(row.revisedQty) }}</td>
                      <td class="cell-profit">{{ formatSignedMw(suggestedSurplus(row)) }}</td>
                      <td>{{ formatPrice(row.contractRate) }}</td>
                      <td class="cell-profit">{{ formatPrice(row.exchangePrice) }}</td>
                      <td class="cell-dsm">
                              <div class="penalty-cell">{{ formatSignedRupees(-row.dsmPenalty) }}<button type="button" class="penalty-info-btn" [attr.title]="penaltyBreakupLabel(row)" aria-label="Show penalty info">i</button></div>
                            </td>
                      <td class="cell-profit"><button type="button" class="calc-value-btn" [attr.title]="contractRevisionNetProfitTooltip(row)" (click)="openContractRevisionNetProfitModal(row)">{{ formatSignedRupees(row.netProfit) }}</button></td>
                      <td><span class="profit-pill" [class.profit-pill-normal]="row.profitability === 'NORMAL'" [class.profit-pill-loss]="row.profitability === 'LOSS'">{{ row.profitability }}</span></td>
                      <td>
                        <button
                          type="button"
                          class="accept-btn"
                          [class.accept-btn-disabled]="!canApprove(row)"
                          [class.accept-btn-approved]="isRevisionApproved(row)"
                          (click)="toggleRevisionApproval(row)"
                        >
                          <lucide-icon [img]="CircleCheckIcon" [size]="13"></lucide-icon>
                          <span>{{ isRevisionApproved(row) ? 'Accepted' : canApprove(row) ? 'Accept' : 'Not Eligible' }}</span>
                        </button>
                      </td>
                    </tr>
                  }
                  }
                </tbody>
              </table>
            </div>
            }
          </article>
        </section>
      } @else if (activeTab() === 'exchange-bids') {
        <section class="exchange-stack">
          <article class="exchange-banner">
            <div class="exchange-banner-icon">
              <lucide-icon [img]="ZapIcon" [size]="18"></lucide-icon>
            </div>
            <div>
              <h2>Approved Exchange Bids - Generated From Approved Contract Revisions</h2>
              <p>Only approved contract revision rows are aggregated per 15-minute time block. Approve profitable revision rows in the Contract Revision tab to move them here as exchange bids.</p>
            </div>
          </article>

          <section class="exchange-kpis">
            <article class="exchange-kpi-card">
              <span>Approved Time Blocks</span>
              <strong>{{ exchangeBidRows().length }}</strong>
              <small>generated from approved revisions</small>
            </article>
            <article class="exchange-kpi-card">
              <span>Approved Aggregated Surplus</span>
              <strong class="kpi-green">{{ totalAggregatedSurplus() | number:'1.0-0' }} MW</strong>
              <small>ready for exchange bids</small>
            </article>
            <article class="exchange-kpi-card">
              <span>Approved Net Profit</span>
              <strong class="kpi-green">{{ formatSignedRupees(totalNetProfit()) }}</strong>
              <small>from approved exchange trading</small>
            </article>
          </section>

          <article class="exchange-table-card">
            <div class="exchange-table-head">
              <div>
                <h2>Approved Exchange Bids by Time Block</h2>
                <p>Each row is created only after contract revision approval.</p>
              </div>
              <div class="exchange-table-actions">
                <label class="bulk-select-toggle">
                  <input
                    type="checkbox"
                    [checked]="allExchangeBidsSelected()"
                    [indeterminate]="hasPartialExchangeBidSelection()"
                    (change)="toggleAllExchangeBidSelections($any($event.target).checked)"
                  />
                  <span>Select All</span>
                </label>
                <button type="button" class="push-bid-btn" (click)="placeSelectedBids()" [disabled]="selectedExchangeBidCount() === 0">
                  <lucide-icon [img]="PlayIcon" [size]="12"></lucide-icon>
                  <span>Place Selected Bids ({{ selectedExchangeBidCount() }})</span>
                </button>
              </div>
            </div>
            <div class="exchange-table-wrap">
              <table class="exchange-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Block #</th>
                    <th>Time Block</th>
                    <th>Total Surplus (MW)</th>
                    <th># Contracts</th>
                    <th>Market</th>
                    <th>Avg Forecast Market Price</th>
                    <th>Exchange Gain (Rs)</th>
                    <th>Penalty (Rs)</th>
                    <th><button type="button" class="calc-header-btn" (click)="openNetProfitFormulaModal('exchange-bid')">Net Profit (Rs)</button></th>
                    <th>Profitability</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @if (loading()) {
                    <tr><td colspan="12" class="empty-state">Loading approved exchange bids...</td></tr>
                  } @else if (exchangeBidRows().length === 0) {
                    <tr><td colspan="12" class="empty-state">No approved exchange bids yet. Approve contract revision rows with positive profit to generate exchange bids.</td></tr>
                  } @else {
                  @for (row of exchangeBidRows(); track row.blockNo + '-' + row.market) {
                    <tr [class.row-selected]="isExchangeBidSelected(row)">
                      <td>
                        <input
                          type="checkbox"
                          class="exchange-select"
                          [checked]="isExchangeBidSelected(row)"
                          (change)="toggleExchangeBidSelection(row, $any($event.target).checked)"
                        />
                      </td>
                      <td class="cell-block">
                        <span class="expand-mark">⌄</span>
                        <span>{{ row.blockNo }}</span>
                      </td>
                      <td>{{ formatMarketTimeBlock(row.timeBlock, row.market) }}</td>
                      <td class="cell-surplus">{{ row.totalSurplusMw }} MW</td>
                      <td><span class="contract-count-pill">{{ row.contractCount }}</span></td>
                      <td>
                        <span class="market-pill" [class.market-rtm]="row.market === 'RTM'">{{ row.market }}</span>
                      </td>
                      <td class="cell-exchange">{{ formatPrice(row.avgExchangePrice) }}</td>
                      <td class="cell-profit">{{ formatSignedRupees(row.exchangeGain) }}</td>
                      <td class="cell-dsm">
                              <div class="penalty-cell">{{ formatSignedRupees(-row.dsmPenalty) }}<button type="button" class="penalty-info-btn" [attr.title]="penaltyBreakupLabel(row)" aria-label="Show penalty info">i</button></div>
                            </td>
                      <td class="cell-profit"><button type="button" class="calc-value-btn" [attr.title]="exchangeBidNetProfitTooltip(row)" (click)="openExchangeBidNetProfitModal(row)">{{ formatSignedRupees(row.netProfit) }}</button></td>
                      <td>
                        <span class="profit-pill" [class.profit-pill-normal]="row.profitability === 'NORMAL'">{{ row.profitability }}</span>
                      </td>
                      <td>
                        <button type="button" class="push-bid-btn" (click)="pushBid(row)">
                          <lucide-icon [img]="PlayIcon" [size]="12"></lucide-icon>
                          <span>Push Bid</span>
                        </button>
                      </td>
                    </tr>
                  }
                  }
                </tbody>
              </table>
            </div>
          </article>
        </section>
      } @else {
        <section class="placeholder-card">
          <div class="placeholder-icon">
            <lucide-icon [img]="FileTextIcon" [size]="26"></lucide-icon>
          </div>
          <div>
            <h2>{{ activeTabLabel() }}</h2>
            <p>
              The remaining workspace can be added here if you need additional drilldown screens beyond the attached designs.
            </p>
          </div>
        </section>
      }

      @if (netProfitModal(); as modal) {
        <div class="calc-modal-backdrop" (click)="closeNetProfitModal()">
          <div class="calc-modal-card" role="dialog" aria-modal="true" aria-label="Net profit calculation" (click)="$event.stopPropagation()">
            <div class="calc-modal-head">
              <div>
                <div class="calc-modal-eyebrow">Net Profit Calculation</div>
                <h2>{{ modal.title }}</h2>
                <p>{{ modal.subtitle }}</p>
              </div>
              <button type="button" class="close-btn" (click)="closeNetProfitModal()">
                <lucide-icon [img]="CloseIcon" [size]="16"></lucide-icon>
              </button>
            </div>
            <div class="calc-modal-body">
              <div class="calc-equation">{{ modal.equation }}</div>
              <p class="calc-modal-hint">{{ modal.hint }}</p>
              <div class="calc-detail-list">
                @for (detail of modal.details; track detail.label) {
                  <div class="calc-detail-row">
                    <span>{{ detail.label }}</span>
                    <strong [class.text-good]="detail.tone === 'good'" [class.text-bad]="detail.tone === 'bad'">{{ detail.value }}</strong>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .hedging-shell{display:grid;gap:.9rem;color:#19324d;}
    .hero-card,.tab-bar,.summary-card,.status-card,.chart-card,.placeholder-card,.info-banner,.revision-card,.exchange-banner,.exchange-kpi-card,.exchange-table-card{
      border:1px solid rgba(20,46,82,.10);
      background:#fff;
      box-shadow:0 14px 32px rgba(15,23,42,.05);
    }
    .hero-card{
      display:flex;
      justify-content:space-between;
      align-items:flex-start;
      gap:1rem;
      padding:1rem 1.1rem;
      border-radius:1rem;
      background:linear-gradient(180deg,#ffffff 0%,#fbfdff 100%);
    }
    .title-row{display:flex;align-items:center;gap:.75rem;flex-wrap:wrap;}
    .hero-copy h1{margin:0;font-size:1.9rem;font-weight:800;color:#16283d;}
    .hero-copy p{margin:.45rem 0 0;color:#4f647b;font-size:.93rem;}
    .hero-meta{display:flex;gap:.45rem;flex-wrap:wrap;margin-top:.8rem;}
    .hero-pill{display:inline-flex;align-items:center;padding:.3rem .65rem;border-radius:999px;background:#eef4ff;color:#23405e;font-size:.76rem;font-weight:700;}
    .guide-link,.toolbar-btn,.run-btn,.tab-btn{display:inline-flex;align-items:center;gap:.45rem;border:1px solid rgba(27,63,102,.14);border-radius:.7rem;background:#fff;color:#23405e;font-weight:600;}
    .guide-link{padding:.3rem .55rem;font-size:.8rem;color:#315efb;border-color:transparent;background:transparent;}
    .hero-actions{display:flex;align-items:flex-end;gap:.6rem;flex-wrap:wrap;justify-content:flex-end;}
    .date-field{display:grid;gap:.28rem;min-width:210px;}
    .market-field{min-width:160px;}
    .date-field span{font-size:.74rem;font-weight:700;color:#667a90;text-transform:uppercase;letter-spacing:.04em;}
    .date-field .form-control,.date-field .form-select{min-height:42px;border-radius:.7rem;border:1px solid rgba(30,58,95,.16);}
    .toolbar-btn,.run-btn{padding:.72rem .9rem;font-size:.84rem;}
    .run-btn{background:#0c4a91;color:#fff;border-color:#0c4a91;}
    .tab-bar{display:flex;gap:.45rem;padding:.8rem 1rem;border-radius:1rem;}
    .tab-btn{padding:.55rem .85rem;border-color:transparent;background:transparent;color:#16283d;font-size:.88rem;}
    .tab-btn.active{background:#0d4a92;color:#fff;}
    .summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.9rem;}
    .summary-card{border-radius:.9rem;padding:.85rem 1rem;min-height:208px;}
    .summary-profit{border-color:rgba(16,185,129,.45);background:linear-gradient(180deg,rgba(240,253,248,.88) 0%,#ffffff 100%);}
    .summary-head{display:flex;align-items:center;justify-content:space-between;font-size:.84rem;color:#65798f;background:#f8fafc;border-radius:.65rem;padding:.55rem .7rem;}
    .summary-body{padding:1rem .15rem 0;display:grid;gap:.95rem;}
    .summary-icon{color:#2563eb;}
    .summary-icon-green{color:#0ea95d;}
    .summary-highlight strong,.profit-value{display:block;font-size:1.55rem;line-height:1;font-weight:800;color:#152b42;}
    .summary-highlight small{display:block;margin-top:.55rem;color:#16a34a;font-size:.9rem;}
    .metric-lines{display:grid;gap:.6rem;}
    .metric-line{display:flex;justify-content:space-between;gap:1rem;padding-bottom:.55rem;border-bottom:1px solid rgba(20,46,82,.08);font-size:.9rem;color:#627488;}
    .metric-line:last-child{border-bottom:0;padding-bottom:0;}
    .metric-line strong{color:#16314e;font-weight:800;}
    .profit-highlight{display:grid;gap:.9rem;}
    .profit-value{font-size:2rem;color:#14b870;}
    .profit-breakup{display:grid;gap:.45rem;font-size:.88rem;color:#627488;}
    .profit-breakup div{display:flex;justify-content:space-between;gap:1rem;}
    .profit-breakup strong{color:#10b981;}
    .text-bad{color:#ef4444 !important;}
    .text-orange{color:#f59e0b !important;}
    .text-good{color:#10b981 !important;}
    .status-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.9rem;}
    .status-card{border-radius:.95rem;padding:.7rem .95rem;display:flex;align-items:center;gap:.8rem;min-height:88px;background:linear-gradient(180deg,#fff 0%,#fbfdff 100%);}
    .status-icon{width:2.7rem;height:2.7rem;border-radius:999px;display:grid;place-items:center;}
    .status-copy strong{display:block;font-size:1.95rem;line-height:1;font-weight:800;}
    .status-copy h3{margin:.16rem 0 0;font-size:.92rem;font-weight:800;}
    .status-copy p{margin:.22rem 0 0;font-size:.78rem;color:#74859a;}
    .tone-green{border-color:rgba(16,185,129,.35);background:linear-gradient(180deg,rgba(236,253,245,.9),#ffffff);}
    .tone-green .status-icon,.tone-green strong{color:#10b981;}
    .tone-blue{border-color:rgba(59,130,246,.3);background:linear-gradient(180deg,rgba(239,246,255,.92),#ffffff);}
    .tone-blue .status-icon,.tone-blue strong{color:#3b82f6;}
    .tone-amber{border-color:rgba(245,158,11,.35);background:linear-gradient(180deg,rgba(255,251,235,.95),#ffffff);}
    .tone-amber .status-icon,.tone-amber strong{color:#f59e0b;}
    .tone-red{border-color:rgba(239,68,68,.28);background:linear-gradient(180deg,rgba(254,242,242,.95),#ffffff);}
    .tone-red .status-icon,.tone-red strong{color:#ef4444;}
    .chart-grid{display:grid;grid-template-columns:1fr 1fr;gap:.9rem;}
    .chart-card{border-radius:.95rem;padding:1rem 1.05rem;min-height:310px;}
    .chart-head h2{margin:0;font-size:1.1rem;font-weight:800;}
    .chart-head p{margin:.22rem 0 0;color:#66788d;font-size:.86rem;}
    .distribution-card{display:grid;place-items:center;height:240px;}
    .pie-wrap{position:relative;width:260px;height:200px;display:grid;place-items:center;}
    .pie-chart{width:192px;height:192px;border-radius:50%;background:conic-gradient(#f59e0b 0 58%, #ef4444 58% 98%, #3b82f6 98% 100%);box-shadow:inset 0 0 0 1px rgba(20,46,82,.06);}
    .pie-label{position:absolute;font-size:.9rem;font-weight:700;}
    .pie-label-orange{bottom:22px;right:22px;color:#f59e0b;}
    .pie-label-red{top:62px;left:20px;color:#ef4444;}
    .pie-label-blue{top:60px;right:6px;color:#3b82f6;}
    .bar-chart{display:grid;gap:1rem;padding-top:1.2rem;}
    .bar-row{display:grid;grid-template-columns:130px 1fr 46px;gap:.8rem;align-items:center;}
    .bar-label{font-size:.83rem;color:#4f647b;}
    .bar-track{position:relative;height:12px;border-radius:999px;background:repeating-linear-gradient(to right,#edf2f7 0 12.5%,#f8fafc 12.5% 25%);}
    .bar-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,#60a5fa,#1d4ed8);}
    .bar-value{font-size:.78rem;color:#7a8a9b;text-align:right;}
    .revision-stack{display:grid;gap:.9rem;}
    .info-banner{display:flex;gap:.75rem;align-items:flex-start;padding:.85rem 1rem;border-radius:.85rem;background:#eff6ff;border-color:rgba(59,130,246,.28);box-shadow:none;}
    .info-icon{width:1.8rem;height:1.8rem;border-radius:999px;display:grid;place-items:center;color:#2563eb;flex:none;}
    .info-banner h2{margin:0;font-size:1rem;font-weight:800;color:#17304c;}
    .info-banner p{margin:.22rem 0 0;font-size:.87rem;color:#35506b;}
    .revision-card{border-radius:.9rem;overflow:hidden;}
    .revision-head{padding:1rem 1rem .8rem;}
    .revision-head h2{margin:0;font-size:1.05rem;font-weight:800;color:#17304c;}
    .revision-head p{margin:.2rem 0 0;font-size:.88rem;color:#6a7c90;}
    .revision-table-wrap{overflow:visible;border-top:1px solid rgba(20,46,82,.08);}
    .revision-accordion-wrap{display:grid;gap:.8rem;padding:1rem;}
    .revision-accordion-card{border:1px solid rgba(20,46,82,.08);border-radius:.9rem;overflow:hidden;background:#fff;}
    .revision-accordion-header{width:100%;display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:.9rem 1rem;border:0;background:linear-gradient(180deg,#f8fbff 0%,#f2f7ff 100%);text-align:left;}
    .revision-accordion-main{display:flex;align-items:center;gap:.8rem;}
    .revision-accordion-main strong{display:block;font-size:.96rem;color:#17304c;}
    .revision-accordion-main small{display:block;color:#6a7c90;font-size:.8rem;}
    .revision-accordion-chevron{font-size:1rem;color:#315efb;}
    .revision-accordion-side{display:grid;gap:.12rem;justify-items:end;font-size:.82rem;color:#4f647b;}
    .revision-accordion-empty{padding:1rem;color:#5f7287;font-weight:600;}
    .revision-table{width:100%;border-collapse:collapse;table-layout:fixed;}
    .revision-table th,.revision-table td{padding:.82rem .75rem;border-bottom:1px solid rgba(20,46,82,.06);white-space:normal;word-break:break-word;font-size:.84rem;vertical-align:middle;}
    .revision-table thead th{background:#f8fafc;color:#475b72;font-size:.8rem;font-weight:800;text-align:left;}
    .revision-table tbody tr{background:linear-gradient(180deg,#f4fff8 0%,#effbf4 100%);}
    .revision-table tbody tr.row-loss{background:linear-gradient(180deg,#fff7f7 0%,#fff1f1 100%);}
    .revision-table tbody tr.row-approved{background:linear-gradient(180deg,#ecfdf5 0%,#e2f8ec 100%);}
    .revision-table td.empty-state{padding:1rem .8rem;text-align:center;white-space:normal;color:#5f7287;font-weight:600;background:#f8fbff;}
    .revision-table-compact .cell-contract{font-weight:800;color:#17304c;}
    .market-pill,.profit-pill{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:.22rem .55rem;font-size:.7rem;font-weight:800;line-height:1.2;}
    .market-pill{background:#e0e7ff;color:#4f46e5;}
    .market-pill.market-rtm{background:#fce7f3;color:#db2777;}
    .cell-profit{color:#10b981;font-weight:800;}
    .cell-revised{color:#f59e0b;font-weight:800;}
    .penalty-cell{font-weight:800;color:#b45309;}
    .penalty-note{display:block;margin-top:.18rem;color:#6a7c90;font-size:.72rem;white-space:normal;line-height:1.3;}
    .profit-pill{background:#fff4db;color:#b45309;border:1px solid rgba(180,83,9,.55);}
    .profit-pill-normal{background:#e0f2fe;color:#0369a1;border-color:rgba(3,105,161,.28);}
    .profit-pill-loss{background:#fee2e2;color:#b91c1c;border-color:rgba(185,28,28,.24);}
    .accept-btn{display:inline-flex;align-items:center;gap:.35rem;padding:.38rem .72rem;border:0;border-radius:.45rem;background:#10b981;color:#fff;font-size:.78rem;font-weight:800;}
    .accept-btn-disabled{background:#94a3b8;}
    .accept-btn-approved{background:#0d4a92;}
    .exchange-stack{display:grid;gap:.9rem;}
    .exchange-banner{display:flex;gap:.8rem;align-items:flex-start;padding:.85rem 1rem;border-radius:.85rem;background:linear-gradient(180deg,#f0fdf4 0%,#f7fff9 100%);border-color:rgba(16,185,129,.28);box-shadow:none;}
    .exchange-banner-icon{width:1.9rem;height:1.9rem;border-radius:999px;display:grid;place-items:center;color:#10b981;flex:none;}
    .exchange-banner h2{margin:0;font-size:1rem;font-weight:800;color:#17304c;}
    .exchange-banner p{margin:.22rem 0 0;font-size:.87rem;color:#35506b;}
    .exchange-kpis{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.9rem;}
    .exchange-kpi-card{padding:.95rem 1rem;border-radius:.85rem;}
    .exchange-kpi-card span{display:block;font-size:.9rem;font-weight:700;color:#17304c;}
    .exchange-kpi-card strong{display:block;margin-top:.75rem;font-size:1.95rem;font-weight:800;color:#1d4ed8;line-height:1;}
    .exchange-kpi-card small{display:block;margin-top:.45rem;color:#5f7287;font-size:.86rem;}
    .exchange-kpi-card .kpi-green{color:#10b981;}
    .exchange-table-card{border-radius:.9rem;overflow:hidden;}
    .exchange-table-head{padding:1rem 1rem .8rem;display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;flex-wrap:wrap;}
    .exchange-table-head h2{margin:0;font-size:1.05rem;font-weight:800;color:#17304c;}
    .exchange-table-head p{margin:.2rem 0 0;font-size:.88rem;color:#6a7c90;}
    .exchange-table-actions{display:flex;align-items:center;gap:.8rem;flex-wrap:wrap;}
    .selection-mode-toggle{display:inline-flex;align-items:center;gap:.35rem;padding:.2rem;border-radius:.8rem;background:#eef4ff;}
    .selection-mode-toggle .tab-btn{padding:.45rem .7rem;font-size:.8rem;}
    .bulk-select-toggle{display:inline-flex;align-items:center;gap:.5rem;font-size:.82rem;font-weight:700;color:#43586f;}
    .bulk-select-toggle input,.exchange-select{accent-color:#0d4a92;}
    .exchange-table-wrap{overflow:auto;border-top:1px solid rgba(20,46,82,.08);}
    .exchange-table{width:100%;border-collapse:collapse;min-width:1400px;}
    .exchange-table th,.exchange-table td{padding:.72rem .8rem;border-bottom:1px solid rgba(20,46,82,.06);white-space:nowrap;font-size:.87rem;}
    .exchange-table thead th{background:#f8fafc;color:#475b72;font-size:.8rem;font-weight:800;text-align:left;}
    .exchange-table tbody tr.row-selected{background:rgba(13,74,146,.05);}
    .cell-block{display:flex;align-items:center;gap:.55rem;}
    .expand-mark{font-size:.82rem;color:#64748b;}
    .contract-count-pill{display:inline-grid;place-items:center;min-width:1.3rem;height:1.3rem;padding:0 .28rem;border-radius:999px;background:#e0e7ff;color:#4f46e5;font-size:.72rem;font-weight:800;}
    .push-bid-btn{display:inline-flex;align-items:center;gap:.35rem;padding:.38rem .72rem;border:0;border-radius:.45rem;background:#0d4a92;color:#fff;font-size:.78rem;font-weight:800;}
    .push-bid-btn:disabled{opacity:.55;cursor:not-allowed;}
    .calc-header-btn,.calc-value-btn{padding:0;border:0;background:transparent;color:inherit;font:inherit;font-weight:inherit;cursor:pointer;}
    .calc-header-btn{text-decoration:underline dotted rgba(35,64,94,.5);text-underline-offset:.18rem;}
    .calc-value-btn{color:inherit;font-weight:800;text-decoration:underline dotted rgba(16,185,129,.5);text-underline-offset:.16rem;}
    .calc-header-btn:hover,.calc-value-btn:hover{color:#0d4a92;}
    .penalty-cell{display:inline-flex;align-items:center;gap:.35rem;}
    .penalty-info-btn{width:1.15rem;height:1.15rem;border:1px solid rgba(13,74,146,.2);border-radius:999px;background:#eef4ff;color:#0d4a92;font-size:.72rem;font-weight:800;line-height:1;display:inline-grid;place-items:center;cursor:pointer;padding:0;}
    .penalty-info-btn:hover{background:#dbeafe;color:#0b3b75;}
    .calc-modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.52);display:flex;align-items:center;justify-content:center;padding:1rem;z-index:1300;}
    .calc-modal-card{width:min(720px,100%);max-height:min(82vh,760px);display:grid;grid-template-rows:auto 1fr;border-radius:1rem;background:#fff;border:1px solid rgba(21,48,84,.08);box-shadow:0 30px 60px rgba(15,23,42,.24);overflow:hidden;}
    .calc-modal-head{display:flex;justify-content:space-between;gap:1rem;padding:1.05rem 1.2rem;border-bottom:1px solid rgba(18,40,72,.08);background:linear-gradient(180deg,#f8fbff,#f1f6fd);}
    .calc-modal-eyebrow{text-transform:uppercase;letter-spacing:.08em;font-size:.72rem;font-weight:800;color:#68809a;}
    .calc-modal-head h2{margin:.15rem 0 0;color:#17314c;font-size:1.25rem;}
    .calc-modal-head p{margin:.28rem 0 0;color:#68809a;font-size:.9rem;}
    .close-btn{border:0;background:#eef2f7;border-radius:999px;width:34px;height:34px;display:grid;place-items:center;color:#23405e;flex:none;}
    .calc-modal-body{overflow:auto;padding:1rem 1.15rem 1.2rem;background:#fff;display:grid;gap:.85rem;}
    .calc-equation{padding:.85rem 1rem;border-radius:.9rem;background:linear-gradient(180deg,#eff6ff,#f8fbff);border:1px solid rgba(59,130,246,.18);font-weight:800;color:#17304c;}
    .calc-modal-hint{margin:0;color:#64788e;font-size:.88rem;}
    .calc-detail-list{display:grid;gap:.65rem;}
    .calc-detail-row{display:flex;justify-content:space-between;gap:1rem;padding:.82rem .9rem;border:1px solid rgba(18,40,72,.08);border-radius:.85rem;background:#fbfdff;}
    .calc-detail-row span{color:#5e748b;}
    .calc-detail-row strong{text-align:right;color:#17304c;}
    .placeholder-card{border-radius:1rem;padding:1.2rem;display:flex;align-items:center;gap:1rem;}
    .placeholder-icon{width:3rem;height:3rem;border-radius:1rem;background:#eff6ff;color:#2563eb;display:grid;place-items:center;}
    .placeholder-card h2{margin:0 0 .3rem;font-size:1.1rem;}
    .placeholder-card p{margin:0;color:#63778d;}
    @media (max-width: 1260px){.summary-grid,.status-grid{grid-template-columns:repeat(2,minmax(0,1fr));}.chart-grid{grid-template-columns:1fr;}.exchange-kpis{grid-template-columns:1fr;}}
    @media (max-width: 860px){.hero-card{flex-direction:column;}.hero-actions{justify-content:flex-start;}.summary-grid,.status-grid{grid-template-columns:1fr;}.tab-bar{overflow:auto;padding:.65rem;}.bar-row{grid-template-columns:110px 1fr 40px;}.pie-wrap{width:100%;}.info-banner{align-items:flex-start;}.revision-head-inline{flex-direction:column;}.revision-table th,.revision-table td{padding:.72rem .55rem;font-size:.78rem;}.penalty-info-btn{width:1rem;height:1rem;font-size:.66rem;}}
  `],
})
export class BessContractHedgingOptimizationPage {
  private static readonly SELLER_MATCH_KEYWORD = 'SECI';

  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly vendorsService = inject(VendorsService);

  protected readonly BookOpenTextIcon = BookOpenText;
  protected readonly CircleCheckIcon = CircleCheck;
  protected readonly FileTextIcon = FileText;
  protected readonly FilterIcon = Filter;
  protected readonly GaugeIcon = Gauge;
  protected readonly HelpIcon = CircleHelp;
  protected readonly InfoIcon = Info;
  protected readonly PlayIcon = Play;
  protected readonly RefreshIcon = RefreshCcw;
  protected readonly TrendingUpIcon = TrendingUp;
  protected readonly CloseIcon = X;
  protected readonly ZapIcon = Zap;

  protected readonly tabs = [
    { id: 'overview' as const, label: 'Overview', icon: Gauge },
    { id: 'contract-revision' as const, label: 'Contract Revision', icon: FileText },
    { id: 'exchange-bids' as const, label: 'Exchange Bids', icon: TrendingUp },
  ];
  protected readonly marketFilterOptions = [
    { value: 'DAM' as const, label: 'DAM' },
    { value: 'RTM' as const, label: 'RTM' },
  ];

  protected readonly activeTab = signal<ViewTab>('contract-revision');
  protected readonly selectedDate = signal(this.defaultDate('DAM'));
  protected readonly selectedMarketFilter = signal<MarketFilter>('DAM');
  protected readonly loading = signal(false);
  protected readonly loadError = signal(false);
  protected readonly snapshot = signal<ContractHedgingSnapshotApi | null>(null);
  protected readonly supportRows = signal<ContractSupportRowApi[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly loas = signal<Loa[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly approvedRevisionKeys = signal<string[]>([]);
  protected readonly selectedRevisionMode = signal<RevisionSelectionMode>('all');
  protected readonly showNotEligibleRevisions = signal(false);
  protected readonly expandedRevisionContracts = signal<string[]>([]);
  protected readonly selectedRevisionKeys = signal<string[]>([]);
  protected readonly selectedExchangeBidKeys = signal<string[]>([]);
  protected readonly netProfitModal = signal<NetProfitModalData | null>(null);

  protected readonly summaryCards = computed<SummaryCard[]>(() => [
    {
      title: 'Total Contracts Analyzed',
      icon: FileText,
      accent: 'blue',
      lines: [],
    },
    {
      title: 'Total Commitment',
      icon: Zap,
      accent: 'blue',
      lines: [
        { label: 'Original:', value: `${Math.round(this.originalCommitment())} MW` },
        { label: 'Revised:', value: `${Math.round(this.revisedCommitment())} MW`, tone: 'orange' },
        { label: 'For Exchange:', value: `${Math.round(this.exchangeCommitment())} MW`, tone: 'green' },
      ],
    },
    {
      title: 'Price Comparison',
      icon: TrendingUp,
      accent: 'blue',
      lines: [
        { label: 'Avg Contract Rate:', value: this.formatUnitRate(this.averageContractRate()) },
        { label: 'Avg Exchange Price:', value: this.formatUnitRate(this.averageExchangePrice()) },
        { label: 'Price Difference:', value: this.formatSignedUnitRate(this.averageExchangePrice() - this.averageContractRate()), tone: 'green' },
      ],
    },
    {
      title: 'Total Net Profit',
      icon: IndianRupee,
      accent: 'green',
      lines: [],
    },
  ]);

  protected readonly contractRevisionRows = computed<ContractRevisionRow[]>(() =>
    (this.snapshot()?.revisions || []).filter(
      (row) => row.bessRole === 'Seller' && this.matchesTargetSeller(this.sellerLabel(row)),
    ),
  );
  protected readonly filteredContractRevisionRows = computed<ContractRevisionRow[]>(() =>
    this.contractRevisionRows().filter((row) => row.market === this.selectedMarketFilter()),
  );
  protected readonly selectableContractRevisionRows = computed<ContractRevisionRow[]>(() =>
    this.filteredContractRevisionRows().filter((row) => this.canApprove(row)),
  );
  protected readonly displayedContractRevisionRows = computed<ContractRevisionRow[]>(() =>
    this.showNotEligibleRevisions() ? this.filteredContractRevisionRows() : this.selectableContractRevisionRows(),
  );
  protected readonly minAllowedDate = computed(() => this.defaultDate(this.selectedMarketFilter()));
  protected readonly selectedRevisionKeySet = computed(() => new Set(this.selectedRevisionKeys()));
  protected readonly selectedRevisionRows = computed(() =>
    this.selectableContractRevisionRows().filter((row) => this.selectedRevisionKeySet().has(this.revisionKey(row))),
  );
  protected readonly expandedRevisionContractSet = computed(() => new Set(this.expandedRevisionContracts()));
  protected readonly contractRevisionGroups = computed<ContractRevisionGroup[]>(() => {
    const groups = new Map<string, ContractRevisionGroup>();
    for (const row of this.displayedContractRevisionRows()) {
      const eligibleCount = this.canApprove(row) ? 1 : 0;
      const existing = groups.get(row.contractId);
      if (existing) {
        existing.rows.push(row);
        existing.eligibleBlockCount += eligibleCount;
        existing.totalBlockCount += 1;
        continue;
      }
      groups.set(row.contractId, {
        contractId: row.contractId,
        seller: this.sellerLabel(row),
        buyer: this.buyerLabel(row),
        eligibleBlockCount: eligibleCount,
        totalBlockCount: 1,
        rows: [row],
      });
    }
    return [...groups.values()].map((group) => ({
      ...group,
      rows: [...group.rows].sort((left, right) => this.timeBlockToNumber(left.timeBlock) - this.timeBlockToNumber(right.timeBlock)),
    }));
  });
  protected readonly selectedRevisionCount = computed(() => this.selectedRevisionRows().length);
  protected readonly allDisplayedRevisionsSelected = computed(() =>
    this.selectableContractRevisionRows().length > 0 &&
    this.selectableContractRevisionRows().every((row) => this.selectedRevisionKeySet().has(this.revisionKey(row))),
  );
  protected readonly hasPartialRevisionSelection = computed(() =>
    this.selectedRevisionCount() > 0 && !this.allDisplayedRevisionsSelected(),
  );
  protected readonly approvedRevisionKeySet = computed(() => new Set(this.approvedRevisionKeys()));
  protected readonly approvedContractRevisionRows = computed(() =>
    this.filteredContractRevisionRows().filter((row) => this.approvedRevisionKeySet().has(this.revisionKey(row))),
  );
  protected readonly exchangeBidRows = computed<ExchangeBidRow[]>(() => this.buildExchangeBidRows(this.approvedContractRevisionRows()));
  protected readonly selectedExchangeBidKeySet = computed(() => new Set(this.selectedExchangeBidKeys()));
  protected readonly selectedExchangeBidRows = computed(() =>
    this.exchangeBidRows().filter((row) => this.selectedExchangeBidKeySet().has(this.exchangeBidKey(row))),
  );
  protected readonly selectedExchangeBidCount = computed(() => this.selectedExchangeBidRows().length);
  protected readonly allExchangeBidsSelected = computed(() =>
    this.exchangeBidRows().length > 0 && this.exchangeBidRows().every((row) => this.selectedExchangeBidKeySet().has(this.exchangeBidKey(row))),
  );
  protected readonly hasPartialExchangeBidSelection = computed(() =>
    this.selectedExchangeBidCount() > 0 && !this.allExchangeBidsSelected(),
  );
  protected readonly sellSupportRows = computed(() =>
    this.supportRows().filter(
      (row) => row.bess_role === 'Seller' && this.matchesTargetSeller(this.supportRowSellerName(row)),
    ),
  );
  protected readonly contractMap = computed(() => {
    const lookup = new Map<string, Contract>();
    for (const contract of this.contracts()) {
      const keys = [contract.id, contract.contract_number].filter((value): value is string => !!value);
      for (const key of keys) {
        if (!lookup.has(key)) lookup.set(key, contract);
      }
    }
    return lookup;
  });
  protected readonly loaMap = computed(() => new Map(this.loas().map((loa) => [loa.id, loa])));
  protected readonly vendorMap = computed(() => new Map(this.vendors().map((vendor) => [vendor.id, vendor])));
  protected readonly supportRowMap = computed(() => {
    const lookup = new Map<string, ContractSupportRowApi>();
    for (const row of this.sellSupportRows()) {
      const keys = [row.contract_number, row.contract_id].filter((value): value is string => !!value);
      for (const key of keys) {
        if (!lookup.has(key)) {
          lookup.set(key, row);
        }
      }
    }
    return lookup;
  });
  protected readonly totalContractsAnalyzed = computed(() => new Set(this.filteredContractRevisionRows().map((row) => row.contractId)).size);
  protected readonly activeSupportContractsCount = computed(() => new Set(this.sellSupportRows().map((row) => row.contract_number)).size);
  protected readonly uniqueBessAssetsCount = computed(() => new Set(this.sellSupportRows().map((row) => row.bess_vendor_name)).size);
  protected readonly approvedRevisionsCount = computed(() => new Set(this.approvedContractRevisionRows().map((row) => row.contractId)).size);
  protected readonly eligibleForRevisionCount = computed(() => new Set(this.filteredContractRevisionRows().filter((row) => this.canApprove(row)).map((row) => row.contractId)).size);
  protected readonly originalCommitment = computed(() => this.filteredContractRevisionRows().reduce((sum, row) => sum + row.originalQty, 0));
  protected readonly revisedCommitment = computed(() => this.filteredContractRevisionRows().reduce((sum, row) => sum + row.revisedQty, 0));
  protected readonly exchangeCommitment = computed(() => this.filteredContractRevisionRows().reduce((sum, row) => sum + this.suggestedSurplus(row), 0));
  protected readonly averageContractRate = computed(() => this.average(this.filteredContractRevisionRows().map((row) => row.contractRate)));
  protected readonly averageExchangePrice = computed(() => this.average(this.filteredContractRevisionRows().map((row) => row.exchangePrice)));
  protected readonly totalExchangeGain = computed(() => this.exchangeBidRows().reduce((sum, row) => sum + row.exchangeGain, 0));
  protected readonly totalDsmPenalty = computed(() => this.exchangeBidRows().reduce((sum, row) => sum + row.dsmPenalty, 0));
  protected readonly totalNetProfit = computed(() => this.exchangeBidRows().reduce((sum, row) => sum + row.netProfit, 0));
  protected readonly totalAggregatedSurplus = computed(() => this.exchangeBidRows().reduce((sum, row) => sum + row.totalSurplusMw, 0));
  protected readonly highProfitCount = computed(() => this.countProfitability('high'));
  protected readonly moderateCount = computed(() => this.countProfitability('moderate'));
  protected readonly marginalCount = computed(() => this.countProfitability('marginal'));
  protected readonly lossCount = computed(() => this.countProfitability('loss'));
  protected readonly statusCards = computed<StatusCard[]>(() => [
    { title: 'High Profit Opportunities', subtitle: 'Net profit above Rs25,000', count: this.highProfitCount(), icon: CircleAlert, tone: 'green' },
    { title: 'Moderate Profit', subtitle: 'Net profit Rs10,000 to Rs25,000', count: this.moderateCount(), icon: Gauge, tone: 'blue' },
    { title: 'Marginal Profit', subtitle: 'Net profit below Rs10,000', count: this.marginalCount(), icon: TriangleAlert, tone: 'amber' },
    { title: 'Not Profitable', subtitle: 'Maintain current contract', count: this.lossCount(), icon: CircleOff, tone: 'red' },
  ]);
  protected readonly topContracts = computed<ProfitBar[]>(() => {
    const rows = [...this.filteredContractRevisionRows()].sort((left, right) => right.netProfit - left.netProfit).slice(0, 5);
    const max = Math.max(...rows.map((row) => row.netProfit), 1);
    return rows.map((row) => ({ contractId: row.contractId, value: Math.max(8, (row.netProfit / max) * 100) }));
  });

  protected readonly activeTabLabel = computed(() => this.tabs.find((tab) => tab.id === this.activeTab())?.label || 'Overview');
  protected readonly overviewEmptyTitle = computed(() => {
    if (this.loadError()) return 'Unable to load optimization';
    return 'No opportunities found';
  });
  protected readonly overviewEmptyMessage = computed(() => {
    if (this.loadError()) {
      return 'The contract hedging optimization data could not be loaded. Refresh the page and try again.';
    }
    return `No ${this.selectedMarketFilter()} opportunities were available for ${this.selectedDate()}.`;
  });
  protected readonly contractRevisionEmptyMessage = computed(() => {
    if (this.loadError()) {
      return 'No record found.';
    }
    return this.showNotEligibleRevisions()
      ? `No ${this.selectedMarketFilter()} rows found.`
      : `No eligible ${this.selectedMarketFilter()} rows found.`;
  });
  protected readonly distributionGradient = computed(() => {
    const total = this.filteredContractRevisionRows().length || 1;
    const highEnd = (this.highProfitCount() / total) * 100;
    const marginalEnd = highEnd + (this.marginalCount() / total) * 100;
    const lossEnd = marginalEnd + (this.lossCount() / total) * 100;
    return `conic-gradient(#3b82f6 0 ${highEnd}%, #f59e0b ${highEnd}% ${marginalEnd}%, #ef4444 ${marginalEnd}% ${lossEnd}%, #cbd5e1 ${lossEnd}% 100%)`;
  });

  constructor() {
    this.loadSupportRows();
    this.loadContracts();
    this.loadLoas();
    this.loadVendors();
    this.load();
  }

  protected setDate(value: string): void {
    const next = value || this.defaultDate(this.selectedMarketFilter());
    this.selectedDate.set(next < this.minAllowedDate() ? this.minAllowedDate() : next);
  }

  protected setMarketFilter(value: string): void {
    const market = this.normalizeMarketFilter(value);
    this.selectedMarketFilter.set(market);
    this.selectedDate.set(this.defaultDate(market));
    this.load();
  }

  protected showGuide(): void {
    this.toast.show('Optimization creates block-wise non-BESS contract revision suggestions using DAM/RTM prices, LOA rates, and LOA penalty conditions. Example: if 90 MW is contracted and 30 MW remains profitable after penalty, the row can suggest 60 MW in contract and 30 MW as surplus for exchange.', '', 'info');
  }

  protected applyFilters(): void {
    this.load();
  }

  protected runOptimization(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.api
      .post<ContractHedgingSnapshotApi>(
        `/api/bess/contract-hedging-optimization/run?trade_date=${encodeURIComponent(this.selectedDate())}&market=${encodeURIComponent(this.selectedMarketFilter())}`,
        {},
      )
      .subscribe({
        next: (snapshot) => {
          this.snapshot.set(snapshot);
          this.approvedRevisionKeys.set([]);
          this.selectedRevisionKeys.set([]);
          this.selectedExchangeBidKeys.set([]);
          this.loadError.set(false);
          this.loading.set(false);
          this.toast.show(`Optimization run stored for ${snapshot.trade_date}`, '', 'success');
        },
        error: () => {
          this.selectedRevisionKeys.set([]);
          this.loadError.set(true);
          this.loading.set(false);
          this.toast.show('Failed to run contract hedging optimization', '', 'danger');
        },
      });
  }

  protected refresh(): void {
    this.load();
  }

  protected canApprove(row: ContractRevisionRow): boolean {
    return row.netProfit > 0 && this.suggestedSurplus(row) > 0 && this.meetsBidLeadTime(row.market, this.timeBlockToNumber(row.timeBlock));
  }

  protected isRevisionApproved(row: ContractRevisionRow): boolean {
    return this.approvedRevisionKeySet().has(this.revisionKey(row));
  }

  protected revisionStatusLabel(row: ContractRevisionRow): string {
    if (this.isRevisionApproved(row)) return 'Approved for Exchange Bid';
    if (this.canApprove(row)) return 'Surplus Suggested';
    return 'Hold Contract Position';
  }

  protected contractSplitLabel(row: ContractRevisionRow): string {
    return `${this.formatMwDetail(row.revisedQty)} MW stays in bilateral contract and ${this.formatMwDetail(this.suggestedSurplus(row))} MW moves as surplus to ${row.market} exchange.`;
  }

  protected setRevisionSelectionMode(mode: RevisionSelectionMode): void {
    this.selectedRevisionMode.set(mode);
    this.selectedRevisionKeys.set([]);
    if (mode === 'contractwise') {
      this.expandedRevisionContracts.set([]);
    }
  }

  protected toggleShowNotEligibleRevisions(checked: boolean): void {
    this.showNotEligibleRevisions.set(checked);
    this.selectedRevisionKeys.set([]);
  }

  protected isRevisionSelected(row: ContractRevisionRow): boolean {
    return this.selectedRevisionKeySet().has(this.revisionKey(row));
  }

  protected isRevisionContractExpanded(contractId: string): boolean {
    return this.expandedRevisionContractSet().has(contractId);
  }

  protected toggleRevisionContract(contractId: string): void {
    const next = new Set(this.expandedRevisionContracts());
    if (next.has(contractId)) next.delete(contractId);
    else next.add(contractId);
    this.expandedRevisionContracts.set([...next]);
  }

  protected isRevisionContractSelected(contractId: string): boolean {
    const rows = this.selectableContractRevisionRows().filter((row) => row.contractId === contractId);
    return rows.length > 0 && rows.every((row) => this.selectedRevisionKeySet().has(this.revisionKey(row)));
  }

  protected isRevisionContractPartiallySelected(contractId: string): boolean {
    const rows = this.selectableContractRevisionRows().filter((row) => row.contractId === contractId);
    const selected = rows.filter((row) => this.selectedRevisionKeySet().has(this.revisionKey(row))).length;
    return selected > 0 && selected < rows.length;
  }

  protected toggleRevisionContractSelection(contractId: string, checked: boolean): void {
    const next = new Set(this.selectedRevisionKeys());
    for (const row of this.selectableContractRevisionRows().filter((item) => item.contractId === contractId)) {
      const key = this.revisionKey(row);
      if (checked) next.add(key);
      else next.delete(key);
    }
    this.selectedRevisionKeys.set([...next]);
  }

  protected toggleRevisionSelection(row: ContractRevisionRow, checked: boolean): void {
    if (!this.canApprove(row)) return;
    const next = new Set(this.selectedRevisionKeys());
    const key = this.revisionKey(row);
    if (checked) next.add(key);
    else next.delete(key);
    this.selectedRevisionKeys.set([...next]);
  }

  protected toggleAllRevisionSelections(checked: boolean): void {
    this.selectedRevisionKeys.set(checked ? this.selectableContractRevisionRows().map((row) => this.revisionKey(row)) : []);
  }

  protected toggleRevisionApproval(row: ContractRevisionRow): void {
    if (!this.canApprove(row)) {
      if (row.market === 'RTM' && !this.meetsBidLeadTime(row.market, this.timeBlockToNumber(row.timeBlock))) {
        this.toast.show(`${row.contractId} cannot be approved because RTM bids must be placed at least 1 hour before the delivery time.`, '', 'info');
      } else {
        this.toast.show(`${row.contractId} cannot be approved because the row has no profitable exchange surplus after the LOA penalty.`, '', 'info');
      }
      return;
    }
    const key = this.revisionKey(row);
    const next = new Set(this.approvedRevisionKeys());
    if (next.has(key)) {
      next.delete(key);
      this.approvedRevisionKeys.set([...next]);
      this.selectedRevisionKeys.set(this.selectedRevisionKeys().filter((item) => item !== key));
      this.selectedExchangeBidKeys.set([]);
      this.toast.show(`${row.contractId} approval removed. The related exchange bid has been removed from Exchange Bids.`, '', 'info');
      return;
    }
    next.add(key);
    this.approvedRevisionKeys.set([...next]);
    this.selectedExchangeBidKeys.set([]);
    this.toast.show(`${row.contractId} approved: ${this.formatMwDetail(row.revisedQty)} MW remains in contract and ${this.formatMwDetail(this.suggestedSurplus(row))} MW moves as surplus to ${row.market} exchange for ${row.timeBlock}.`, '', 'success');
  }

  protected approveSelectedRevisions(): void {
    const rows = this.selectedRevisionRows();
    if (rows.length === 0) {
      this.toast.show('Select at least one contract revision row to approve.', '', 'info');
      return;
    }
    const next = new Set(this.approvedRevisionKeys());
    for (const row of rows) next.add(this.revisionKey(row));
    this.approvedRevisionKeys.set([...next]);
    this.selectedRevisionKeys.set([]);
    this.selectedExchangeBidKeys.set([]);
    this.toast.show(`${rows.length} selected contract revisions approved and moved to Exchange Bids.`, '', 'success');
  }

  protected approveAllEligible(): void {
    const eligibleRows = this.filteredContractRevisionRows().filter((row) => this.canApprove(row));
    this.approvedRevisionKeys.set(eligibleRows.map((row) => this.revisionKey(row)));
    this.selectedRevisionKeys.set([]);
    this.selectedExchangeBidKeys.set([]);
    this.toast.show(`${eligibleRows.length} profitable contract revisions approved and moved to Exchange Bids.`, '', 'success');
  }

  protected isExchangeBidSelected(row: ExchangeBidRow): boolean {
    return this.selectedExchangeBidKeySet().has(this.exchangeBidKey(row));
  }

  protected toggleExchangeBidSelection(row: ExchangeBidRow, checked: boolean): void {
    const next = new Set(this.selectedExchangeBidKeys());
    const key = this.exchangeBidKey(row);
    if (checked) next.add(key);
    else next.delete(key);
    this.selectedExchangeBidKeys.set([...next]);
  }

  protected toggleAllExchangeBidSelections(checked: boolean): void {
    this.selectedExchangeBidKeys.set(checked ? this.exchangeBidRows().map((row) => this.exchangeBidKey(row)) : []);
  }

  protected placeSelectedBids(): void {
    const rows = this.selectedExchangeBidRows();
    if (rows.length === 0) {
      this.toast.show('Select at least one approved exchange bid to place.', '', 'info');
      return;
    }
    const totalSurplus = rows.reduce((sum, row) => sum + row.totalSurplusMw, 0);
    const totalNetProfit = rows.reduce((sum, row) => sum + row.netProfit, 0);
    this.toast.show(
      `Prepared ${rows.length} exchange bids with ${Math.round(totalSurplus)} MW total surplus and expected net ${this.formatSignedRupees(totalNetProfit)}.`,
      '',
      'success',
    );
    this.selectedExchangeBidKeys.set([]);
  }

  protected pushBid(row: ExchangeBidRow): void {
    if (!this.meetsBidLeadTime(row.market, row.blockNo)) {
      this.toast.show(`RTM bid for block ${row.blockNo} (${row.timeBlock}) cannot be prepared because RTM bids must be placed at least 1 hour before the delivery time.`, '', 'info');
      return;
    }
    this.toast.show(
      `Prepared ${row.market} bid for block ${row.blockNo} (${row.timeBlock}) with ${row.totalSurplusMw} MW from approved contract revisions and expected net ${this.formatSignedRupees(row.netProfit)}.`,
      '',
      'success',
    );
  }

  protected openNetProfitFormulaModal(scope: 'contract-revision' | 'exchange-bid'): void {
    const revisionScope = scope === 'contract-revision';
    this.netProfitModal.set({
      title: revisionScope ? 'Contract Revision Net Profit' : 'Exchange Bid Net Profit',
      subtitle: revisionScope
        ? 'Net profit is calculated for each contract row in the selected market.'
        : 'Net profit is calculated for each approved exchange bid block in the selected market.',
      equation: 'Net Profit = Exchange Gain - Penalty',
      hint: revisionScope
        ? 'Click any net profit value in the Contract Revision table to see row-wise numbers and the exact values used.'
        : 'Click any net profit value in the Exchange Bids table to see the aggregated numbers for that block.',
      details: revisionScope
        ? [
            { label: 'Exchange Gain', value: 'Surplus for Exchange x Price Spread Used x 250 ', tone: 'good' },
            { label: 'Penalty', value: 'Surplus MW x LOA penalty rate x 250', tone: 'bad' },
            { label: 'Net Profit', value: 'Exchange Gain - Penalty', tone: 'good' },
          ]
        : [
            { label: 'Exchange Gain', value: 'Sum of approved contract revision gains in the block', tone: 'good' },
            { label: 'Penalty', value: 'Sum of approved LOA penalty deductions in the block', tone: 'bad' },
            { label: 'Net Profit', value: 'Exchange Gain - Penalty', tone: 'good' },
          ],
    });
  }

  protected openContractRevisionNetProfitModal(row: ContractRevisionRow): void {
    const spreadUsed = Math.max(row.exchangePrice - row.contractRate, 0);
    const riskRate = row.penaltyRate || 0;
    const suggestedSurplus = this.suggestedSurplus(row);
    const hasExchangeSurplus = suggestedSurplus > 0;
    this.netProfitModal.set({
      title: `${row.contractId} Net Profit`,
      subtitle: `${this.formatMarketTimeBlock(row.timeBlock, row.market)} • ${row.market} • ${this.sellerLabel(row)} to ${this.buyerLabel(row)}`,
      equation: hasExchangeSurplus
        ? `${this.formatSignedRupees(row.netProfit)} = ${this.formatSignedRupees(row.exchangeGain)} - ${this.formatRupees(row.dsmPenalty)}`
        : `${this.formatRupees(0)} = No exchange diversion`,
      hint: `${row.recommendation}: ${row.strategy}`,
      details: [
        { label: hasExchangeSurplus ? 'Surplus for Exchange' : 'Exchange Diversion', value: hasExchangeSurplus ? `${this.formatMwDetail(suggestedSurplus)} MW` : 'No surplus suggested' },
        { label: 'Contract Rate', value: `${this.formatPrice(row.contractRate)}/unit` },
        { label: 'Forecast Market Price', value: `${this.formatPrice(row.exchangePrice)}/unit`, tone: 'good' },
        { label: 'Price Spread Used', value: this.formatSignedUnitRate(spreadUsed), tone: spreadUsed > 0 ? 'good' : 'bad' },
        {
          label: 'Exchange Gain',
          value: hasExchangeSurplus
            ? `${this.formatSignedRupees(row.exchangeGain)} = ${this.formatMwDetail(suggestedSurplus)} x ${this.formatPrice(spreadUsed)} x 250`
            : `${this.formatRupees(0)} = No surplus moved to exchange`,
          tone: hasExchangeSurplus ? 'good' : 'default',
        },
        { label: 'LOA Penalty Rate', value: `${this.formatPrice(riskRate)}/unit`, tone: 'bad' },
        {
          label: 'LOA Penalty',
          value: hasExchangeSurplus
            ? `${this.formatSignedRupees(-row.dsmPenalty)} = ${this.formatMwDetail(suggestedSurplus)} x ${this.formatPrice(riskRate)} x 250`
            : `${this.formatRupees(0)} = No exchange surplus, so no penalty applied`,
          tone: hasExchangeSurplus ? 'bad' : 'default',
        },
        { label: 'Net Profit', value: hasExchangeSurplus ? this.formatSignedRupees(row.netProfit) : this.formatRupees(row.netProfit), tone: row.netProfit >= 0 ? 'good' : 'bad' },
      ],
    });
  }

  protected openExchangeBidNetProfitModal(row: ExchangeBidRow): void {
    this.netProfitModal.set({
      title: `${row.market} Exchange Bid Net Profit`,
      subtitle: `Block ${row.blockNo} • ${this.formatMarketTimeBlock(row.timeBlock, row.market)}`,
      equation: `${this.formatSignedRupees(row.netProfit)} = ${this.formatSignedRupees(row.exchangeGain)} - ${this.formatRupees(row.dsmPenalty)}`,
      hint: `This block combines ${row.contractCount} approved contract revision ${row.contractCount === 1 ? 'row' : 'rows'} into one exchange bid.`,
      details: [
        { label: 'Total Surplus', value: `${this.formatMwDetail(row.totalSurplusMw)} MW` },
        { label: 'Contracts Aggregated', value: `${row.contractCount}` },
        { label: 'Avg Forecast Market Price', value: `${this.formatPrice(row.avgExchangePrice)}/unit`, tone: 'good' },
        { label: 'Exchange Gain', value: this.formatSignedRupees(row.exchangeGain), tone: 'good' },
        { label: 'Penalty', value: this.formatSignedRupees(-row.dsmPenalty), tone: 'bad' },
        { label: 'Net Profit', value: this.formatSignedRupees(row.netProfit), tone: row.netProfit >= 0 ? 'good' : 'bad' },
      ],
    });
  }

  protected closeNetProfitModal(): void {
    this.netProfitModal.set(null);
  }

  protected defaultDate(market: MarketFilter): string {
    const date = new Date();
    if (market === 'DAM') {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString().slice(0, 10);
  }

  protected formatRupees(value: number): string {
    return `Rs${Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0'}`;
  }

  protected formatMw(value: number): string {
    return `${Number.isFinite(value) ? Math.round(value) : 0}`;
  }

  protected formatMwDetail(value: number): string {
    if (!Number.isFinite(value)) return '0';
    return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  }

  protected formatSignedMw(value: number): string {
    const amount = Number.isFinite(value) ? value : 0;
    return `${amount >= 0 ? '+' : ''}${this.formatMwDetail(amount)}`;
  }

  protected formatMarketTimeBlock(value: string, market: 'DAM' | 'RTM'): string {
    const raw = (value || '').trim();
    if (!raw) return 'NA';
    if (raw.includes('-')) return raw;
    const [hourPart, minutePart] = raw.split(':');
    const hour = Number(hourPart);
    const minute = Number(minutePart);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return raw;
    const start = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const durationMinutes = market === 'DAM' ? 60 : 15;
    const totalMinutes = hour * 60 + minute + durationMinutes;
    const endHour = Math.floor((totalMinutes % (24 * 60)) / 60);
    const endMinute = totalMinutes % 60;
    const end = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
    return `${start}-${end}`;
  }

  protected formatPrice(value: number): string {
    return Number.isFinite(value) ? value.toFixed(2) : '0.00';
  }

  protected formatUnitRate(value: number): string {
    return `${this.formatPrice(value)}/unit`;
  }

  protected formatSignedUnitRate(value: number): string {
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${this.formatPrice(Math.abs(value))}/unit`;
  }

  protected formatSignedRupees(value: number): string {
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${this.formatRupees(Math.abs(value))}`;
  }

  protected formatCompactRupees(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 100000) return `${value < 0 ? '-' : ''}Rs${(abs / 100000).toFixed(2)}L`;
    if (abs >= 1000) return `${value < 0 ? '-' : ''}Rs${(abs / 1000).toFixed(1)}K`;
    return `${value < 0 ? '-' : ''}Rs${abs.toFixed(0)}`;
  }

  protected formatSignedCompactRupees(value: number): string {
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${this.formatCompactRupees(Math.abs(value)).replace(/^-/, '')}`;
  }

  protected topContractValue(contractId: string): number {
    return this.filteredContractRevisionRows().find((row) => row.contractId === contractId)?.netProfit || 0;
  }

  protected contractTypeLabel(row: ContractRevisionRow): string {
    return this.supportRowMap().get(row.contractId)?.contract_type || 'Contract';
  }

  protected contractCategoryLabel(row: ContractRevisionRow): string {
    return this.supportRowMap().get(row.contractId)?.contract_category || 'General';
  }

  protected contractPeriodLabel(row: ContractRevisionRow): string {
    const support = this.supportRowMap().get(row.contractId);
    if (!support) return `Trade date ${this.selectedDate()}`;
    return `${this.formatDate(support.period_from)} to ${this.formatDate(support.period_to)}`;
  }

  protected agreementTypeLabel(row: ContractRevisionRow): string {
    return this.supportRowMap().get(row.contractId)?.agreement_type || 'Agreement N/A';
  }

  protected sellerLabel(row: ContractRevisionRow): string {
    return this.contractPartyName(row, 'sell') || this.supportSellerName(row) || row.bessAsset || row.client || 'NA';
  }

  protected buyerLabel(row: ContractRevisionRow): string {
    return this.contractPartyName(row, 'buy') || this.supportBuyerName(row) || row.client || 'NA';
  }

  protected penaltyBreakupLabel(row: { recommendation?: string; strategy?: string; dsmPenalty: number }): string {
    const note = [row.recommendation, row.strategy].filter((value) => !!value).join(' | ');
    return note || 'LOA penalty adjustment';
  }

  protected contractRevisionNetProfitTooltip(row: ContractRevisionRow): string {
    const surplus = this.suggestedSurplus(row);
    const spreadUsed = Math.max(row.exchangePrice - row.contractRate, 0);
    return `Net Profit = Exchange Gain - Penalty\n${this.formatSignedRupees(row.netProfit)} = ${this.formatSignedRupees(row.exchangeGain)} - ${this.formatRupees(row.dsmPenalty)}\nExchange Gain: ${this.formatMwDetail(surplus)} x ${this.formatPrice(spreadUsed)} x 250\nPenalty: ${this.formatMwDetail(surplus)} x ${this.formatPrice(row.penaltyRate || 0)} x 250`;
  }

  protected exchangeBidNetProfitTooltip(row: ExchangeBidRow): string {
    return `Net Profit = Exchange Gain - Penalty\n${this.formatSignedRupees(row.netProfit)} = ${this.formatSignedRupees(row.exchangeGain)} - ${this.formatRupees(row.dsmPenalty)}`;
  }


  protected energyFlowLabel(row: ContractRevisionRow): string {
    return this.supportRowMap().get(row.contractId)?.energy_flow || 'Energy flow N/A';
  }


  protected snapshotPeriodLabel(): string {
    const snapshot = this.snapshot();
    if (snapshot?.generated_at) return `Generated ${this.formatDateTime(snapshot.generated_at)}`;
    return `Trade date ${this.selectedDate()}`;
  }

  private load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.api
      .get<ContractHedgingSnapshotApi>('/api/bess/contract-hedging-optimization', {
        trade_date: this.selectedDate(),
        market: this.selectedMarketFilter(),
      })
      .subscribe({
        next: (snapshot) => {
          this.snapshot.set(snapshot);
          this.approvedRevisionKeys.set([]);
          this.selectedRevisionKeys.set([]);
          this.selectedExchangeBidKeys.set([]);
          this.loadError.set(false);
          this.loading.set(false);
        },
        error: () => {
          this.snapshot.set(null);
          this.approvedRevisionKeys.set([]);
          this.selectedRevisionKeys.set([]);
          this.selectedExchangeBidKeys.set([]);
          this.loadError.set(true);
          this.loading.set(false);
          this.toast.show('Failed to load contract hedging data', '', 'danger');
        },
      });
  }

  private loadSupportRows(): void {
    this.api.get<ContractSupportRowApi[]>('/api/contracts-support/all-vendors').subscribe({
      next: (rows) => this.supportRows.set(rows || []),
      error: () => this.supportRows.set([]),
    });
  }

  private loadContracts(): void {
    this.contractsService.list().subscribe({
      next: (rows) => this.contracts.set(rows || []),
      error: () => this.contracts.set([]),
    });
  }

  private loadLoas(): void {
    this.loaService.list().subscribe({
      next: (rows) => this.loas.set(rows || []),
      error: () => this.loas.set([]),
    });
  }

  private loadVendors(): void {
    this.vendorsService.list().subscribe({
      next: (rows) => this.vendors.set(rows || []),
      error: () => this.vendors.set([]),
    });
  }

  private contractPartyName(row: ContractRevisionRow, kind: 'buy' | 'sell'): string {
    const contract = this.contractMap().get(row.contractId);
    const parties = contract?.party_details || [];
    const resolvedKinds = parties.map((party) => this.resolveContractPartyKind(party));
    const exactMatchIndex = resolvedKinds.findIndex((partyKind) => partyKind === kind);
    let match = exactMatchIndex >= 0 ? parties[exactMatchIndex] : undefined;

    if (!match && parties.length === 2) {
      const oppositeMatchIndex = resolvedKinds.findIndex((partyKind) => !!partyKind && partyKind !== kind);
      if (oppositeMatchIndex >= 0) {
        match = parties[oppositeMatchIndex === 0 ? 1 : 0];
      }
    }

    if (!match) {
      match = kind === 'buy' ? parties[1] || parties[0] : parties[0];
    }

    const loaId = String(match?.loa_id || '').trim();
    if (loaId) {
      const loa = this.loaMap().get(loaId);
      const loaVendorId = String(loa?.vendor_id || '').trim();
      const linkedVendorName = loaVendorId ? this.vendorMap().get(loaVendorId)?.vendor_name || '' : '';
      if (linkedVendorName) return linkedVendorName;
      if (loa?.loa_number) return loa.loa_number;
    }

    const vendorId = String(match?.vendor_id || '').trim();
    return vendorId ? this.vendorMap().get(vendorId)?.vendor_name || '' : '';
  }

  private resolveContractPartyKind(party: { party_type?: string | null; loa_id?: string | null } | undefined): 'buy' | 'sell' | null {
    const explicitPartyKind = this.normalizePartyKind(party?.party_type);
    if (explicitPartyKind) return explicitPartyKind;
    const loaId = String(party?.loa_id || '').trim();
    if (!loaId) return null;
    return this.normalizePartyKind(this.loaMap().get(loaId)?.category);
  }

  private normalizePartyKind(value: string | null | undefined): 'buy' | 'sell' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return null;
    if (normalized.includes('buy') || normalized.includes('purchase')) return 'buy';
    if (normalized.includes('sell') || normalized.includes('sale')) return 'sell';
    return null;
  }

  private supportSellerName(row: ContractRevisionRow): string {
    const support = this.supportRowMap().get(row.contractId);
    if (!support) return '';
    return support.bess_role === 'Seller' ? support.bess_vendor_name || '' : support.counterparty_vendor_name || '';
  }

  private supportRowSellerName(row: ContractSupportRowApi): string {
    return row.bess_role === 'Seller' ? row.bess_vendor_name || '' : row.counterparty_vendor_name || '';
  }

  private matchesTargetSeller(value: string | null | undefined): boolean {
    return String(value || '')
      .trim()
      .toUpperCase()
      .includes(BessContractHedgingOptimizationPage.SELLER_MATCH_KEYWORD);
  }

  protected suggestedSurplus(row: ContractRevisionRow): number {
    const surplus = Number.isFinite(row.surplus) ? row.surplus : NaN;
    if (Number.isFinite(surplus)) {
      return Math.max(0, Number(surplus.toFixed(2)));
    }
    const originalQty = Number.isFinite(row.originalQty) ? row.originalQty : 0;
    const revisedQty = Number.isFinite(row.revisedQty) ? row.revisedQty : 0;
    return Math.max(0, Number((originalQty - revisedQty).toFixed(2)));
  }

  private supportBuyerName(row: ContractRevisionRow): string {
    const support = this.supportRowMap().get(row.contractId);
    if (!support) return '';
    return support.bess_role === 'Seller' ? support.counterparty_vendor_name || '' : support.bess_vendor_name || '';
  }

  private countProfitability(category: ProfitCategory): number {
    return this.filteredContractRevisionRows().filter((row) => this.profitCategory(row.netProfit) === category).length;
  }

  private normalizeMarketFilter(value: string): MarketFilter {
    return value === 'RTM' ? 'RTM' : 'DAM';
  }

  private profitCategory(netProfit: number): ProfitCategory {
    if (netProfit < 0) return 'loss';
    if (netProfit === 0) return 'normal';
    if (netProfit > 25000) return 'high';
    if (netProfit >= 10000) return 'moderate';
    return 'marginal';
  }

  private profitabilityLabel(netProfit: number): ExchangeBidRow['profitability'] {
    if (netProfit < 0) return 'LOSS';
    if (netProfit === 0) return 'NORMAL';
    if (netProfit > 25000) return 'HIGH';
    if (netProfit >= 10000) return 'MODERATE';
    return 'MARGINAL';
  }

  private revisionKey(row: ContractRevisionRow): string {
    return `${row.contractId}-${row.timeBlock}-${row.market}`;
  }

  private exchangeBidKey(row: ExchangeBidRow): string {
    return `${row.blockNo}-${row.market}-${row.timeBlock}`;
  }

  private buildExchangeBidRows(rows: ContractRevisionRow[]): ExchangeBidRow[] {
    const groups = new Map<string, {
      blockNo: number;
      timeBlock: string;
      totalSurplusMw: number;
      contractCount: number;
      market: 'DAM' | 'RTM';
      avgExchangePriceWeighted: number;
      exchangeGain: number;
      dsmPenalty: number;
      netProfit: number;
    }>();

    for (const row of rows) {
      if (!this.canApprove(row)) continue;
      const key = `${row.market}-${row.timeBlock}`;
      const blockNo = this.timeBlockToNumber(row.timeBlock);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          blockNo,
          timeBlock: row.timeBlock,
          totalSurplusMw: this.suggestedSurplus(row),
          contractCount: 1,
          market: row.market,
          avgExchangePriceWeighted: row.exchangePrice * this.suggestedSurplus(row),
          exchangeGain: row.exchangeGain,
          dsmPenalty: row.dsmPenalty,
          netProfit: row.netProfit,
        });
        continue;
      }
      existing.totalSurplusMw += this.suggestedSurplus(row);
      existing.contractCount += 1;
      existing.avgExchangePriceWeighted += row.exchangePrice * this.suggestedSurplus(row);
      existing.exchangeGain += row.exchangeGain;
      existing.dsmPenalty += row.dsmPenalty;
      existing.netProfit += row.netProfit;
    }

    return [...groups.values()]
      .map((item) => ({
        blockNo: item.blockNo,
        timeBlock: item.timeBlock,
        totalSurplusMw: Number(item.totalSurplusMw.toFixed(2)),
        contractCount: item.contractCount,
        market: item.market,
        avgExchangePrice: Number((item.avgExchangePriceWeighted / Math.max(item.totalSurplusMw, 1)).toFixed(2)),
        exchangeGain: Number(item.exchangeGain.toFixed(0)),
        dsmPenalty: Number(item.dsmPenalty.toFixed(0)),
        netProfit: Number(item.netProfit.toFixed(0)),
        profitability: this.profitabilityLabel(item.netProfit),
      }))
      .filter((row) => this.meetsBidLeadTime(row.market, row.blockNo))
      .sort((left, right) => right.netProfit - left.netProfit || left.blockNo - right.blockNo);
  }

  private meetsBidLeadTime(market: 'DAM' | 'RTM', blockNo: number): boolean {
    if (market !== 'RTM') return true;

    const selectedDate = new Date(`${this.selectedDate()}T00:00:00`);
    if (Number.isNaN(selectedDate.getTime())) return true;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tradeDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    if (tradeDate.getTime() > today.getTime()) return true;
    if (tradeDate.getTime() < today.getTime()) return false;

    const deliveryStart = new Date(tradeDate.getTime() + (Math.max(0, blockNo - 1) * 15 * 60 * 1000));
    return deliveryStart.getTime() - now.getTime() >= 60 * 60 * 1000;
  }

  private average(values: number[]): number {
    const valid = values.filter((value) => Number.isFinite(value));
    if (!valid.length) return 0;
    return Number((valid.reduce((sum, value) => sum + value, 0) / valid.length).toFixed(2));
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) return 'NA';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private formatDateTime(value: string | null | undefined): string {
    if (!value) return 'NA';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private timeBlockToNumber(timeBlock: string): number {
    const [start] = (timeBlock || '').split('-');
    const [hour, minute] = start.split(':').map((part) => Number(part));
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 1;
    return Math.min(96, Math.max(1, Math.floor((hour * 60 + minute) / 15) + 1));
  }
}
